import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircle, faPen, faVideo } from '@/fontawesome';
import type { DayTradingStreamInfo } from '@/hooks/useDayTradingWebSocket';
import type { StreamStatus } from '@/hooks/useStreamStatus';
import { getStreamExternalURL, hasStreamPiP, isStreamConfigured } from '@/utils/stream-info';

interface StreamHeaderButtonProps {
  streamInfo: DayTradingStreamInfo;
  sessionClosed: boolean;
  /**
   * 'moderator' — always visible when not NONE; shows live status + edit pen.
   * 'viewer'    — only shows when the PiP is minimized or when mode has no PiP.
   */
  variant: 'moderator' | 'viewer';
  /** PiP is currently minimized. Ignored for modes that have no PiP. */
  minimized?: boolean;
  /** Triggered when the user clicks the live-status button (YOUTUBE/DIRECT). */
  onOpenPiP?: () => void;
  /** Moderator-only: open the stream-edit modal. */
  onEdit?: () => void;
  /** Live status for YOUTUBE/DIRECT. Ignored for other modes. */
  status?: StreamStatus;
}

const STATUS_BG: Record<'live' | 'error' | 'other', string> = {
  live:  'bg-green-600 text-white hover:bg-green-700',
  error: 'bg-red-600 text-white hover:bg-red-700',
  other: 'bg-gray-500 text-white hover:bg-gray-600',
};

function statusLabel(status: StreamStatus, mode: DayTradingStreamInfo['mode']): string {
  if (status === 'live')  return 'Live';
  if (status === 'error') return 'Fehler';
  return mode === 'DIRECT' ? 'Offline' : 'VOD';
}

export default function StreamHeaderButton({
  streamInfo,
  sessionClosed,
  variant,
  minimized = false,
  onOpenPiP,
  onEdit,
  status = 'offline',
}: StreamHeaderButtonProps) {
  const configured = isStreamConfigured(streamInfo);
  const externalURL = getStreamExternalURL(streamInfo);
  const isGoogleMeet = streamInfo.mode === 'GOOGLE_MEET';
  const isDiscord = streamInfo.mode === 'DISCORD';
  const showEditPen = variant === 'moderator' && !sessionClosed && onEdit !== undefined;

  // Moderator on a closed session: just a link to re-open the recording/stream
  // externally — no PiP toggle, no edit pen. Viewer variant on closed sessions
  // keeps the regular PiP-toggle behavior (so VOD playback still works).
  if (sessionClosed && variant === 'moderator') {
    if (!configured || externalURL === '') return null;
    return (
      <a
        href={externalURL}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg bg-gray-500 text-white hover:bg-gray-600 cursor-pointer transition-colors"
        title="Stream öffnen"
      >
        <FontAwesomeIcon icon={faVideo} className="w-4 h-4" />
        Stream
      </a>
    );
  }

  // Google Meet / Discord: always-visible link (no PiP exists to toggle).
  if (isGoogleMeet || isDiscord) {
    const label = isGoogleMeet ? 'Google Meet' : 'Discord';
    const linkColor = isGoogleMeet
      ? 'bg-blue-600 hover:bg-blue-700'
      : 'bg-indigo-600 hover:bg-indigo-700';
    if (!configured) {
      if (variant !== 'moderator') return null;
      return (
        <div className="flex items-center gap-1">
          <DisabledStreamSpan />
          {showEditPen && <EditPen onClick={onEdit!} />}
        </div>
      );
    }
    return (
      <div className="flex items-center gap-1">
        <a
          href={externalURL}
          target="_blank"
          rel="noopener noreferrer"
          className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg text-white cursor-pointer transition-colors ${linkColor}`}
          title={`${label} öffnen`}
        >
          <FontAwesomeIcon icon={faVideo} className="w-4 h-4" />
          {label}
        </a>
        {showEditPen && <EditPen onClick={onEdit!} />}
      </div>
    );
  }

  // YOUTUBE / DIRECT — has a PiP.
  if (!hasStreamPiP(streamInfo)) {
    // NONE
    if (variant !== 'moderator') return null;
    return (
      <div className="flex items-center gap-1">
        <DisabledStreamSpan />
        {showEditPen && <EditPen onClick={onEdit!} />}
      </div>
    );
  }

  // Viewer: only show the button when the PiP is hidden.
  if (variant === 'viewer') {
    if (!configured || !minimized) return null;
    return (
      <button
        onClick={onOpenPiP}
        className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer transition-colors"
      >
        <FontAwesomeIcon icon={faVideo} className="w-4 h-4" />
        Stream
      </button>
    );
  }

  // Moderator
  if (!configured) {
    return (
      <div className="flex items-center gap-1">
        <DisabledStreamSpan />
        {showEditPen && <EditPen onClick={onEdit!} />}
      </div>
    );
  }

  const bucket = status === 'live' ? 'live' : status === 'error' ? 'error' : 'other';
  return (
    <div className="flex items-center gap-1">
      <button
        onClick={onOpenPiP}
        className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg cursor-pointer transition-colors ${STATUS_BG[bucket]}`}
        title={status === 'live' ? 'Stream anzeigen (Live)' : status === 'error' ? 'Stream anzeigen (Fehler)' : 'Stream anzeigen (Offline)'}
      >
        <FontAwesomeIcon icon={faCircle} className={`w-2.5 h-2.5 ${status === 'live' ? 'animate-pulse' : ''}`} />
        {statusLabel(status, streamInfo.mode)}
      </button>
      {showEditPen && <EditPen onClick={onEdit!} />}
    </div>
  );
}

function DisabledStreamSpan() {
  return (
    <span
      className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-500"
      title="Kein Stream konfiguriert"
    >
      <FontAwesomeIcon icon={faVideo} className="w-4 h-4" />
      Stream
    </span>
  );
}

function EditPen({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="p-1.5 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 cursor-pointer transition-colors"
      title="Stream bearbeiten"
    >
      <FontAwesomeIcon icon={faPen} className="w-3.5 h-3.5" />
    </button>
  );
}
